import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import '../styles/Dashboard.css';

interface Booking { id: string; sitterName: string; sitterEmail: string; parentName: string; parentEmail: string; date: string; startTime: string; endTime: string; childName: string; childAge: string; address: string; allergies: string; specialInstructions: string; timestamp: string; status: string; }
interface Order { id: string; parentEmail: string; parentName: string; itemName: string; price: number; quantity?: number; totalPrice?: number; address?: string; deliveryDate?: string; deliveryType?: string; status: string; timestamp: string; }
interface MessageReply { id: string; user: string; comment: string; timestamp: string; }
interface ContactMessage { id: string; name: string; email: string; message: string; replies: MessageReply[]; timestamp: string; }
interface Event { id: string; title: string; date: string; time: string; location: string; description: string; }

const Dashboard: React.FC = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [contactMessages, setContactMessages] = useState<ContactMessage[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Sitter-only states
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [eventFormData, setEventFormData] = useState({ title: '', date: '', time: '', location: '', description: '' });

  const fetchDashboardData = useCallback(() => {
    fetch(`${API_URL}/api/bookings`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json()).then((data: Booking[]) => setBookings(Array.isArray(data) ? data.reverse() : []));

    fetch(`${API_URL}/api/orders`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json()).then((data: Order[]) => { /* unused */ });

    fetch(`${API_URL}/api/events`)
      .then(res => res.json()).then((data: Event[]) => setEvents(Array.isArray(data) ? data.reverse() : []));

    if (user?.id.startsWith('sitter-')) {
      fetch(`${API_URL}/api/contact`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.json()).then((data: ContactMessage[]) => setContactMessages(Array.isArray(data) ? data.reverse() : []));

      fetch(`${API_URL}/api/apps/me`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.json()).then(data => { if (!data.error) { /* unused profile data */ } });
    }
    setLoading(false);
  }, [token, user?.id]);

  useEffect(() => {
    if (!authLoading && user && token) {
      fetchDashboardData();
      const interval = setInterval(fetchDashboardData, 10000); 
      return () => clearInterval(interval);
    }
  }, [authLoading, user, token, fetchDashboardData]);

  const handleOpenEventForm = (event?: Event) => {
    if (event) {
      setEditingEvent(event);
      setEventFormData({ title: event.title, date: event.date, time: event.time, location: event.location, description: event.description });
    } else {
      setEditingEvent(null);
      setEventFormData({ title: '', date: '', time: '', location: '', description: '' });
    }
    setShowEventForm(true);
  };

  const handleEventSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const url = editingEvent ? `${API_URL}/api/events/${editingEvent.id}` : `${API_URL}/api/events`;
    const method = editingEvent ? 'PUT' : 'POST';

    fetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(eventFormData)
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to save event');
        return data;
      })
      .then(() => {
        setShowEventForm(false);
        setEditingEvent(null);
        setEventFormData({ title: '', date: '', time: '', location: '', description: '' });
        fetchDashboardData();
      })
      .catch(err => {
        console.error('Error saving event:', err);
        alert(err.message);
      });
  };

  const handleDeleteEvent = (id: string) => {
    if (!window.confirm('Delete this event?')) return;
    fetch(`${API_URL}/api/events/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(() => fetchDashboardData());
  };

  if (authLoading || (loading && user)) return <div className="dashboard-container">Loading dashboard...</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div>
          <h1>Welcome, {user.name.split(' ')[0]}!</h1>
          <p>Logged in as <strong>{user.id.startsWith('sitter-') ? 'Sitter' : 'Parent'}</strong></p>
        </div>
        {user.id.startsWith('sitter-') && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="secondary-btn" onClick={() => handleOpenEventForm()}>Add Event</button>
          </div>
        )}
      </header>

      {showEventForm && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h2>{editingEvent ? 'Edit Event' : 'Add Upcoming Event'}</h2>
            <form onSubmit={handleEventSubmit} className="profile-form">
              <div className="form-group"><label>Event Title</label><input type="text" required value={eventFormData.title} onChange={e => setEventFormData({...eventFormData, title: e.target.value})} /></div>
              <div className="form-group"><label>Date</label><input type="text" placeholder="e.g. April 15, 2026" required value={eventFormData.date} onChange={e => setEventFormData({...eventFormData, date: e.target.value})} /></div>
              <div className="form-group"><label>Time</label><input type="text" placeholder="e.g. 10:00 AM - 2:00 PM" required value={eventFormData.time} onChange={e => setEventFormData({...eventFormData, time: e.target.value})} /></div>
              <div className="form-group"><label>Location</label><input type="text" required value={eventFormData.location} onChange={e => setEventFormData({...eventFormData, location: e.target.value})} /></div>
              <div className="form-group"><label>Description</label><textarea required value={eventFormData.description} onChange={e => setEventFormData({...eventFormData, description: e.target.value})} /></div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowEventForm(false)}>Cancel</button>
                <button type="submit" className="save-btn">{editingEvent ? 'Update Event' : 'Post Event'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CUSTOMER INQUIRIES SECTION */}
      {user.id.startsWith('sitter-') && (
        <div className="bookings-list" style={{ marginBottom: '40px' }}>
          <h2>💬 Customer Inquiries</h2>
          {contactMessages.length === 0 ? <p>No active inquiries.</p> : contactMessages.map(msg => (
            <div key={msg.id} className="booking-item-card" style={{ borderLeft: '6px solid #3498db' }}>
              <div className="booking-main">
                <div className="booking-info-group">
                  <label>Customer</label>
                  <span>{msg.name} ({msg.email})</span>
                </div>
                <div className="booking-info-group">
                  <label>Latest Message</label>
                  <span style={{ fontStyle: 'italic' }}>"{msg.message}"</span>
                </div>
              </div>
              <div className="booking-actions">
                <button 
                  className="view-btn" 
                  onClick={() => navigate('/messaging', { state: { chatId: msg.id, name: msg.name, email: msg.email } })}
                >
                  Respond to Chat
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MY BOOKINGS */}
      <div className="bookings-list">
        <h2>My Bookings</h2>
        {bookings.length === 0 ? <p>No bookings yet.</p> : bookings.map(b => (
          <div key={b.id} className="booking-item-card">
            <div className={`booking-status ${b.status.toLowerCase()}`}>{b.status}</div>
            <div className="booking-main">
              <div className="booking-info-group"><label>Customer</label><span>{b.parentName}</span></div>
              <div className="booking-info-group"><label>Time</label><span>{b.date} @ {b.startTime}</span></div>
            </div>
          </div>
        ))}
      </div>

      {user.id.startsWith('sitter-') && (
        <div className="bookings-list" style={{ marginTop: '40px' }}>
          <h2>Event Manager</h2>
          {events.length === 0 ? <p>No events posted.</p> : events.map(e => (
            <div key={e.id} className="booking-item-card">
              <div className="booking-main">
                <div className="booking-info-group"><label>Event</label><span>{e.title}</span></div>
                <div className="booking-info-group"><label>Date</label><span>{e.date}</span></div>
              </div>
              <div className="booking-actions" style={{ display: 'flex', gap: '10px' }}>
                <button className="view-btn" onClick={() => handleOpenEventForm(e)}>Edit Event</button>
                <button className="decline-btn" onClick={() => handleDeleteEvent(e.id)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
